import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { ProductUnknown } from "@/common/contracts/product.contracts";
import { CHECKOUT_PACKAGES_URL } from "@/common/static/checkout/checkout.static";
import { BoostCard } from "@/features/create-ad-packages/components/BoostCard";
import {
  CreateAdPackagesProvider,
  useCreateAdPackages,
} from "@/features/create-ad-packages/components/CreateAdPackagesProvider";
import { NO_BOOST_DAYS } from "@/features/create-ad-packages/hooks/usePackageCartSelection";
import { Button } from "@/ui/cva/Button";

type BoostAdModalContentProps = {
  product?: ProductUnknown;
  onCancel: () => void;
};

/** Boost-only picker for the single-ad boost action. Reuses the packages cart
 * so checkout is identical to the create-ad flow. No "No boost" option and no
 * preselection: the free default (NO_BOOST_DAYS) simply renders no selected card. */
const BoostAdModalBody = ({ onCancel }: { onCancel: () => void }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const {
    productId,
    boostPackages,
    selectedBoostDays,
    selectBoost,
    isMutating,
  } = useCreateAdPackages();

  // Selection is applied optimistically, so `isMutating` keeps the user from
  // reaching checkout before the cart mutation lands — on an empty cart.
  const canProceed = selectedBoostDays !== NO_BOOST_DAYS && !isMutating;

  const handleProceed = () => {
    if (productId) {
      router.push(`${CHECKOUT_PACKAGES_URL}/${productId}`);
      onCancel();
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        {boostPackages.map(pkg => (
          <BoostCard
            key={pkg.days}
            pkg={pkg}
            selected={selectedBoostDays === pkg.days}
            onSelect={() => selectBoost(pkg.days)}
          />
        ))}
      </div>
      <div className="flex flex-col-reverse gap-2 md:grid md:grid-cols-2">
        <Button appearance="outlined" fill onClick={onCancel}>
          {t("account-ads:cancel", "Annuleren")}
        </Button>
        <Button fill disabled={!canProceed} onClick={handleProceed}>
          {t("boost-ad:boost-proceed-to-checkout", "Ga naar de kassa")}
        </Button>
      </div>
    </div>
  );
};

export const BoostAdModalContent = ({
  product,
  onCancel,
}: BoostAdModalContentProps) => (
  <CreateAdPackagesProvider product={product}>
    <BoostAdModalBody onCancel={onCancel} />
  </CreateAdPackagesProvider>
);
